import apiV2 from '../../api/v2';
import { toApiError } from '../apiErrors';

export interface MonitoringServiceResult {
    success: boolean;
    message?: string;
    error?: string;
}

/**
 * Service for patient monitoring actions
 */
export class MonitoringService {
    /**
     * Sends DM app activation link to patient
     */
    static async sendDMActivationLink(patientId: string): Promise<MonitoringServiceResult> {
        try {
            await apiV2.post(`/patients/${patientId}/dm/monitoring/activation-link`);
            return {
                success: true,
                message: 'DM activation link sent successfully'
            };
        } catch (error: unknown) {
            return {
                success: false,
                error: toApiError(error).message
            };
        }
    }

    /**
     * Starts DM monitoring via v2 API
     */
    static async startDMMonitoring(
        patientId: string,
        quickstartId: string
    ): Promise<MonitoringServiceResult> {
        try {
            await apiV2.post(`/patients/${patientId}/dm/monitoring/start`, { quickstartId });
            return {
                success: true,
                message: 'Monitoring started successfully'
            };
        } catch (error: unknown) {
            return {
                success: false,
                error: toApiError(error).message
            };
        }
    }

    /**
     * Pauses DM monitoring via v2 API.
     * `resumeDate` is a unix timestamp in seconds, chosen by the caller (e.g.
     * from ManageMonitoringModal's resume-date picker) — the backend does not
     * apply a default, so every caller must supply one.
     */
    static async pauseDMMonitoring(
        patientId: string,
        resumeDate: number
    ): Promise<MonitoringServiceResult> {
        try {
            await apiV2.post(`/patients/${patientId}/dm/monitoring/pause`, { resumeDate });
            return {
                success: true,
                message: 'Monitoring paused successfully'
            };
        } catch (error: unknown) {
            return {
                success: false,
                error: toApiError(error).message
            };
        }
    }

    /**
     * Resumes DM monitoring via v2 API
     */
    static async resumeDMMonitoring(patientId: string): Promise<MonitoringServiceResult> {
        try {
            await apiV2.post(`/patients/${patientId}/dm/monitoring/resume`);
            return {
                success: true,
                message: 'Monitoring resumed successfully'
            };
        } catch (error: unknown) {
            return {
                success: false,
                error: toApiError(error).message
            };
        }
    }

    /**
     * Stops DM monitoring via v2 API
     */
    static async stopDMMonitoring(patientId: string): Promise<MonitoringServiceResult> {
        try {
            await apiV2.post(`/patients/${patientId}/dm/monitoring/stop`);
            return {
                success: true,
                message: 'Monitoring stopped successfully'
            };
        } catch (error: unknown) {
            return {
                success: false,
                error: toApiError(error).message
            };
        }
    }
}
